// import { createHash } from "crypto";

import settings from 'config/settings'

// Allowed aspect ratios for video uploads
const ALLOWED_ASPECT_RATIOS = [
  { ratio: 16 / 9, label: '16:9' },
  { ratio: 40 / 27, label: '40:27' },
  { ratio: 427 / 240, label: '427:240' }
]

/**
 * Validates if a video file has an allowed aspect ratio
 * @param videoFile - The video file to validate
 * @returns Promise with validation result
 */
async function validateVideoAspectRatio (
  videoFile: File
): Promise<{ valid: boolean; aspectRatio?: string; message?: string }> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'metadata'

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src)
      const { videoWidth, videoHeight } = video

      if (!videoWidth || !videoHeight) {
        resolve({
          valid: false,
          message: 'Unable to determine video dimensions'
        })
        return
      }

      const videoAspectRatio = videoWidth / videoHeight
      const currentAspectRatioLabel = `${videoWidth}:${videoHeight}`

      // Check if the video aspect ratio matches any of the allowed ratios (with a small tolerance)
      const tolerance = 0.01
      const isValid = ALLOWED_ASPECT_RATIOS.some(
        ({ ratio }) => Math.abs(videoAspectRatio - ratio) < tolerance
      )

      if (isValid) {
        resolve({
          valid: true,
          aspectRatio: currentAspectRatioLabel
        })
      } else {
        const allowedRatiosText = ALLOWED_ASPECT_RATIOS.map(({ label }) => label).join(', ')
        resolve({
          valid: false,
          aspectRatio: currentAspectRatioLabel,
          message: `Invalid aspect ratio (${currentAspectRatioLabel}). Only ${allowedRatiosText} are allowed.`
        })
      }
    }

    video.onerror = () => {
      resolve({
        valid: false,
        message: 'Failed to load video metadata'
      })
    }

    video.src = URL.createObjectURL(videoFile)
  })
}

async function handleVideoUpload (videoFile, onProgress) {
  // Retrieve the JWT object from localStorage
  const jwt = JSON.parse(localStorage.getItem("jwt"));
  const sanitizedFileName = videoFile.name.replace(/\s+/g, "_");

  if (!jwt || !jwt.accessToken) {
    console.error("JWT not found or invalid structure in localStorage");
    return;
  }

  // Construct the authorization token
  const token = `${jwt.tokenType} ${jwt.accessToken}`;
  // const chunkSize = 100 * 1024 * 1024; // 100 MB per part
  // const totalParts = Math.ceil(videoFile.size / chunkSize);
  // let uploadedSize = 0;

  if (onProgress) onProgress(7); // Ensure progress starts at 7%

  // Step 1: Start the multipart upload
  const response = await fetch(
    `${settings.mediaUploadUrl}/start-multipart-upload?fileName=${encodeURIComponent(
      sanitizedFileName
    )}&contentType=${encodeURIComponent(videoFile.type)}`,
    {
      method: "POST",
      mode: "cors",
      headers: {
        authorization: token,
        "Content-Type": "application/json",
      },
    }
  );
  const { uploadUrl } = await response.json();

  // Step 2: Get resumable upload URL
  async function getResumableUploadUrl(uploadUrl) {

    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: { "x-goog-resumable": "start", "Access-Control-Allow-Origin": "http://localhost:3000" },
    });
    if (!response.ok) {
      throw new Error(`Failed to initiate upload: ${response.statusText}`);
    }
    return response.headers.get("location");
  }

  // Step 3: Upload file chunks
  async function uploadFileChunk(uploadUrl, file, start, chunkSize, totalSize) {
    const end = Math.min(start + chunkSize, totalSize);
    const chunk = file.slice(start, end);

    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Range": `bytes ${start}-${end - 1}/${totalSize}`,
      },
      body: chunk,
    });

    if (!response.ok && response.status !== 308) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  // Upload file in chunks with progress tracking
  async function uploadInChunks(uploadUrl, file, chunkSize = 100 * 1024 * 1024) {
    const totalSize = file.size;
    const resumableUrl = await getResumableUploadUrl(uploadUrl);

    let start = 0;
    while (start < totalSize) {
      await uploadFileChunk(resumableUrl, file, start, chunkSize, totalSize);
      start += chunkSize;

      // Update progress percentage
      if (onProgress) {
        onProgress(Math.round((start / totalSize) * 100));
      }
    }

    if (onProgress) onProgress(100);
  }

  await uploadInChunks(uploadUrl, videoFile)

  // Step 4: Complete the upload
  const completeResponse = await fetch(
    `${settings.mediaUploadUrl}/complete-multipart-upload?fileName=${encodeURIComponent(
      sanitizedFileName
    )}&uploadId=${encodeURIComponent(uploadUrl)}`,
    {
      method: "POST",
      mode: "cors",
      headers: {
        authorization: token,
        "Content-Type": "application/json",
      },
    }
  );

  const result = await completeResponse.json();

  if (onProgress) onProgress(100); // Ensure progress is 100% at completion

  return result;
}

// Helper function to compute MD5 hash
// async function computeMD5(blob) {
//   const arrayBuffer = await blob.arrayBuffer();
//   const hash = createHash("md5");
//   hash.update(Buffer.from(arrayBuffer));
//   return hash.digest("hex");
// }

export { handleVideoUpload, validateVideoAspectRatio }
